import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails
} from 'amazon-cognito-identity-js';
import { apiClient } from '../services/api';

const AuthContext = createContext(null);

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
};

const userPool = new CognitoUserPool(poolData);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper to map Cognito user to app user
  const mapCognitoUser = (session) => {
    const idToken = session.getIdToken();
    const payload = idToken.payload;
    const groups = payload['cognito:groups'] || [];

    return {
      id: payload.sub,
      email: payload.email,
      firstName: payload.given_name || '',
      lastName: payload.family_name || '',
      role: groups.includes('Admins') ? 'admin' : 'member',
      token: idToken.getJwtToken(),
      groups
    };
  };

  // Check for existing session on mount
  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession((err, session) => {
        if (err || !session.isValid()) {
          setIsLoading(false);
          return;
        }
        const authUser = mapCognitoUser(session);
        setUser(authUser);
        localStorage.setItem('sdul_user', JSON.stringify(authUser));
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    setIsLoading(true);

    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session) => {
          const authUser = mapCognitoUser(session);
          setUser(authUser);
          localStorage.setItem('sdul_user', JSON.stringify(authUser));
          setIsLoading(false);
          resolve(authUser);
        },
        onFailure: (err) => {
          setError(err.message || JSON.stringify(err));
          setIsLoading(false);
          reject(err);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // For MVP, we'll just fail or you'd handle the challenge
          setError('New password required');
          setIsLoading(false);
          reject(new Error('New password required'));
        }
      });
    });
  }, []);

  const logout = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    setUser(null);
    localStorage.removeItem('sdul_user');
    setError(null);
  }, []);

  const forgotPassword = useCallback(async (email) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: () => resolve({ success: true }),
        onFailure: (err) => {
          setError(err.message);
          reject(err);
        },
      });
    }).finally(() => setIsLoading(false));
  }, []);

  const updateProfile = useCallback(async (updates) => {
    setIsLoading(true);
    try {
      await apiClient.put('/profile', {
        firstName: updates.firstName,
        lastName: updates.lastName,
        phoneNumber: updates.telephone,
        country: updates.country,
      });
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('sdul_user', JSON.stringify(updatedUser));
      return updatedUser;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const isAuthenticated = !!user;
  const isMember = isAuthenticated; // All authenticated users are members
  const isAdmin = isAuthenticated && user?.role === 'admin';

  const value = {
    user,
    isLoading,
    error,
    isAuthenticated,
    isMember,
    isAdmin,
    login,
    logout,
    forgotPassword,
    updateProfile,
    setError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
